/**
 * Hook for fetching and managing admin orders using URQL.
 * Encapsulates filtering, pagination, and data transformation.
 */

import { useState, useCallback, useMemo } from "react";
import { useQuery } from "urql";
import type { AdminOrder, OrdersFilter } from "../types.ts";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const AdminOrdersDocument = `
  query AdminOrders($filter: AdminOrdersFilter, $limit: Int) {
    adminOrders(filter: $filter, limit: $limit) {
      orders {
        id
        status
        totalAmount
        currency
        createdAt
        customer {
          id
          firstName
          lastName
          username
        }
        event {
          id
          title
          startsAt
          imageUrl
        }
        items {
          id
          quantity
          unitPrice
          ticketType {
            id
            name
          }
        }
        tickets {
          id
          ticketNumber
          status
        }
      }
      totalCount
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface AdminOrdersResult {
  orders: AdminOrder[];
  totalCount: number;
}

interface UseAdminOrdersOptions {
  /** Initial filter */
  initialFilter?: OrdersFilter;
  /** Limit per page */
  limit?: number;
  /** Whether to pause the query */
  pause?: boolean;
}

interface UseAdminOrdersResult {
  orders: AdminOrder[];
  totalCount: number;
  isLoading: boolean;
  error: string | null;
  filter: OrdersFilter;
  setFilter: (filter: OrdersFilter) => void;
  refresh: () => void;
  /** Orders grouped by event */
  ordersByEvent: Map<string, AdminOrder[]>;
}

// =============================================================================
// HOOK
// =============================================================================

export function useAdminOrders(
  options: UseAdminOrdersOptions = {}
): UseAdminOrdersResult {
  const { initialFilter = {}, limit = 200, pause = false } = options;

  const [filter, setFilterState] = useState<OrdersFilter>(initialFilter);

  // Build filter input for GraphQL
  const filterInput = useMemo(() => {
    const input: Record<string, unknown> = {};
    if (filter.status) input.status = filter.status;
    if (filter.eventId) input.eventId = filter.eventId;
    if (filter.search?.trim()) input.search = filter.search.trim();
    return Object.keys(input).length > 0 ? input : undefined;
  }, [filter]);

  const [result, reexecuteQuery] = useQuery<{ adminOrders: AdminOrdersResult }>(
    {
      query: AdminOrdersDocument,
      variables: { filter: filterInput, limit },
      pause,
    }
  );

  const orders = result.data?.adminOrders?.orders ?? [];
  const totalCount = result.data?.adminOrders?.totalCount ?? 0;

  const setFilter = useCallback((newFilter: OrdersFilter) => {
    setFilterState(newFilter);
  }, []);

  const refresh = useCallback(() => {
    reexecuteQuery({ requestPolicy: "network-only" });
  }, [reexecuteQuery]);

  // Group orders by event, sorted by event date (newest first)
  const ordersByEvent = useMemo(() => {
    const map = new Map<string, AdminOrder[]>();

    for (const order of orders) {
      const eventId = order.event.id;
      const existing = map.get(eventId);
      if (existing) {
        existing.push(order);
      } else {
        map.set(eventId, [order]);
      }
    }

    // Sort entries by event start date (newest first)
    const entries = Array.from(map.entries());
    entries.sort(([, a], [, b]) => {
      const timeA = new Date(a[0].event.startsAt).getTime();
      const timeB = new Date(b[0].event.startsAt).getTime();
      return timeB - timeA;
    });

    return new Map(entries);
  }, [orders]);

  return {
    orders,
    totalCount,
    isLoading: result.fetching,
    error: result.error?.message ?? null,
    filter,
    setFilter,
    refresh,
    ordersByEvent,
  };
}

/**
 * Hook for fetching orders for a specific event.
 */
export function useEventOrders(eventId: string) {
  return useAdminOrders({
    initialFilter: { eventId },
  });
}
